import { CommentInterface } from 'modules/comments/components/comment/comment.types'

type OrderedIdsState = {
  headId: number | null
  tailId: number | null
  prevIdById: Map<number, number | null>
  nextIdById: Map<number, number | null>
}

type CommentAnchor =
  | { type: 'head' }
  | { anchorId: number; type: 'after' | 'before' }
  | { type: 'tail' }

type CommentFragment = {
  comments: CommentInterface[]
  firstComment: CommentInterface
  serverIndex: number
  lastComment: CommentInterface
}

const buildUniqueServerComments = (serverComments: CommentInterface[]): CommentInterface[] => {
  const uniqueComments: CommentInterface[] = []
  const seenCommentIds = new Set<number>()

  for (let index = serverComments.length - 1; index >= 0; index -= 1) {
    const comment = serverComments[index]!

    if (seenCommentIds.has(comment.id)) {
      continue
    }

    seenCommentIds.add(comment.id)
    uniqueComments.push(comment)
  }

  uniqueComments.reverse()
  return uniqueComments
}

const appendFragment = (mergedComments: CommentInterface[], fragment: CommentFragment): void => {
  fragment.comments.forEach(comment => {
    mergedComments.push(comment)
  })
}

const pushFragment = (
  fragmentsByAnchorId: Map<number, CommentFragment[]>,
  anchorId: number,
  fragment: CommentFragment,
): void => {
  const fragments = fragmentsByAnchorId.get(anchorId)

  if (fragments) {
    fragments.push(fragment)
    return
  }

  fragmentsByAnchorId.set(anchorId, [fragment])
}

const buildCommentFragments = (comments: CommentInterface[]): CommentFragment[] | null => {
  const commentById = new Map<number, CommentInterface>()
  const serverIndexById = new Map<number, number>()
  const nextCommentByPrevId = new Map<number, CommentInterface>()

  comments.forEach((comment, index) => {
    commentById.set(comment.id, comment)
    serverIndexById.set(comment.id, index)
  })

  for (const comment of comments) {
    if (comment.prevId === null || !commentById.has(comment.prevId)) {
      continue
    }

    if (nextCommentByPrevId.has(comment.prevId)) {
      return null
    }

    nextCommentByPrevId.set(comment.prevId, comment)
  }

  const visitedIds = new Set<number>()
  const fragments: CommentFragment[] = []

  comments.forEach(comment => {
    if (comment.prevId !== null && commentById.has(comment.prevId)) {
      return
    }

    if (visitedIds.has(comment.id)) {
      return
    }

    const comments = [comment]
    let currentComment = comment
    let serverIndex = serverIndexById.get(comment.id) ?? Number.MAX_SAFE_INTEGER

    visitedIds.add(comment.id)

    while (true) {
      const nextComment = nextCommentByPrevId.get(currentComment.id)
      if (!nextComment) {
        break
      }

      if (visitedIds.has(nextComment.id)) {
        return null
      }

      visitedIds.add(nextComment.id)
      comments.push(nextComment)
      currentComment = nextComment
      serverIndex = Math.min(serverIndex, serverIndexById.get(nextComment.id) ?? serverIndex)
    }

    fragments.push({
      comments,
      firstComment: comment,
      serverIndex,
      lastComment: currentComment,
    })
  })

  if (visitedIds.size !== comments.length) {
    return null
  }

  fragments.sort(
    (leftFragment, rightFragment) => leftFragment.serverIndex - rightFragment.serverIndex,
  )

  return fragments
}

const tryMergeCommentFragments = (
  mergedLocalComments: CommentInterface[],
  comments: CommentInterface[],
  localCommentIds: Set<number>,
): CommentInterface[] | null => {
  const fragments = buildCommentFragments(comments)
  if (!fragments) {
    return null
  }

  const frontFragments: CommentFragment[] = []
  const endFragments: CommentFragment[] = []
  const fragmentsByPrevId = new Map<number, CommentFragment[]>()
  const fragmentsByNextId = new Map<number, CommentFragment[]>()
  const unresolvedCommentIds = new Set<number>()

  fragments.forEach(fragment => {
    const headComment = fragment.firstComment
    const tailComment = fragment.lastComment

    if (headComment.prevId === null) {
      frontFragments.push(fragment)
      return
    }

    if (localCommentIds.has(headComment.prevId)) {
      pushFragment(fragmentsByPrevId, headComment.prevId, fragment)
      return
    }

    if (tailComment.nextId === null) {
      endFragments.push(fragment)
      return
    }

    if (localCommentIds.has(tailComment.nextId)) {
      pushFragment(fragmentsByNextId, tailComment.nextId, fragment)
      return
    }

    fragment.comments.forEach(comment => unresolvedCommentIds.add(comment.id))
  })

  const mergedComments: CommentInterface[] = []

  frontFragments.forEach(fragment => appendFragment(mergedComments, fragment))

  mergedLocalComments.forEach(localComment => {
    const beforeFragments = fragmentsByNextId.get(localComment.id)
    if (beforeFragments) {
      beforeFragments.forEach(fragment => appendFragment(mergedComments, fragment))
    }

    mergedComments.push(localComment)

    const afterFragments = fragmentsByPrevId.get(localComment.id)
    if (!afterFragments) {
      return
    }

    afterFragments.forEach(fragment => appendFragment(mergedComments, fragment))
  })

  endFragments.forEach(fragment => appendFragment(mergedComments, fragment))

  comments.forEach(comment => {
    if (unresolvedCommentIds.has(comment.id)) {
      mergedComments.push(comment)
    }
  })

  return mergedComments
}

const createOrderedIdsState = (comments: CommentInterface[]): OrderedIdsState => {
  const prevIdById = new Map<number, number | null>()
  const nextIdById = new Map<number, number | null>()

  comments.forEach((comment, index) => {
    prevIdById.set(comment.id, comments[index - 1]?.id ?? null)
    nextIdById.set(comment.id, comments[index + 1]?.id ?? null)
  })

  return {
    headId: comments[0]?.id ?? null,
    tailId: comments.at(-1)?.id ?? null,
    prevIdById,
    nextIdById,
  }
}

const getCommentAnchor = (
  comment: CommentInterface,
  renderedIds: Set<number>,
): CommentAnchor | null => {
  if (comment.prevId === null) {
    return { type: 'head' }
  }

  if (renderedIds.has(comment.prevId)) {
    return { anchorId: comment.prevId, type: 'after' }
  }

  if (comment.nextId === null) {
    return { type: 'tail' }
  }

  if (renderedIds.has(comment.nextId)) {
    return { anchorId: comment.nextId, type: 'before' }
  }

  return null
}

const insertAtHead = (orderedIds: OrderedIdsState, commentId: number): void => {
  if (orderedIds.headId === null) {
    orderedIds.headId = commentId
    orderedIds.tailId = commentId
    orderedIds.prevIdById.set(commentId, null)
    orderedIds.nextIdById.set(commentId, null)
    return
  }

  const currentHeadId = orderedIds.headId

  orderedIds.headId = commentId
  orderedIds.prevIdById.set(commentId, null)
  orderedIds.nextIdById.set(commentId, currentHeadId)
  orderedIds.prevIdById.set(currentHeadId, commentId)
}

const insertAtTail = (orderedIds: OrderedIdsState, commentId: number): void => {
  if (orderedIds.tailId === null) {
    orderedIds.headId = commentId
    orderedIds.tailId = commentId
    orderedIds.prevIdById.set(commentId, null)
    orderedIds.nextIdById.set(commentId, null)
    return
  }

  const currentTailId = orderedIds.tailId

  orderedIds.tailId = commentId
  orderedIds.prevIdById.set(commentId, currentTailId)
  orderedIds.nextIdById.set(commentId, null)
  orderedIds.nextIdById.set(currentTailId, commentId)
}

const insertAfter = (orderedIds: OrderedIdsState, anchorId: number, commentId: number): void => {
  const nextId = orderedIds.nextIdById.get(anchorId) ?? null

  orderedIds.prevIdById.set(commentId, anchorId)
  orderedIds.nextIdById.set(commentId, nextId)
  orderedIds.nextIdById.set(anchorId, commentId)

  if (nextId === null) {
    orderedIds.tailId = commentId
    return
  }

  orderedIds.prevIdById.set(nextId, commentId)
}

const insertBefore = (orderedIds: OrderedIdsState, anchorId: number, commentId: number): void => {
  const prevId = orderedIds.prevIdById.get(anchorId) ?? null

  orderedIds.prevIdById.set(commentId, prevId)
  orderedIds.nextIdById.set(commentId, anchorId)
  orderedIds.prevIdById.set(anchorId, commentId)

  if (prevId === null) {
    orderedIds.headId = commentId
    return
  }

  orderedIds.nextIdById.set(prevId, commentId)
}

const insertCommentId = (
  orderedIds: OrderedIdsState,
  commentId: number,
  anchor: CommentAnchor,
): void => {
  switch (anchor.type) {
    case 'head': {
      insertAtHead(orderedIds, commentId)
      return
    }
    case 'tail': {
      insertAtTail(orderedIds, commentId)
      return
    }
    case 'after': {
      insertAfter(orderedIds, anchor.anchorId, commentId)
      return
    }
    case 'before': {
      insertBefore(orderedIds, anchor.anchorId, commentId)
      return
    }
  }
}

const appendDependentId = (
  map: Map<number, number[]>,
  anchorId: number,
  dependentId: number,
): void => {
  const dependentIds = map.get(anchorId)

  if (dependentIds) {
    dependentIds.push(dependentId)
    return
  }

  map.set(anchorId, [dependentId])
}

const materializeRenderedComments = (
  orderedIds: OrderedIdsState,
  commentsById: Map<number, CommentInterface>,
): CommentInterface[] => {
  const mergedComments: CommentInterface[] = []
  let currentId = orderedIds.headId

  while (currentId !== null) {
    const comment = commentsById.get(currentId)

    if (!comment) {
      throw new Error(`Comment ${currentId} is missing from the merged comments map`)
    }

    mergedComments.push(comment)
    currentId = orderedIds.nextIdById.get(currentId) ?? null
  }

  return mergedComments
}

const mergeCommentsWithDependencyQueue = (
  mergedLocalComments: CommentInterface[],
  comments: CommentInterface[],
): CommentInterface[] => {
  const mergedCommentById = new Map(mergedLocalComments.map(comment => [comment.id, comment]))
  const orderedIds = createOrderedIdsState(mergedLocalComments)
  const renderedIds = new Set(mergedLocalComments.map(comment => comment.id))
  const pendingCommentById = new Map(comments.map(comment => [comment.id, comment]))
  const dependentIdsByPrevId = new Map<number, number[]>()
  const dependentIdsByNextId = new Map<number, number[]>()
  const candidateIds: number[] = []
  const queuedCandidateIds = new Set<number>()

  const enqueueCandidate = (commentId: number): void => {
    if (!pendingCommentById.has(commentId) || queuedCandidateIds.has(commentId)) {
      return
    }

    queuedCandidateIds.add(commentId)
    candidateIds.push(commentId)
  }

  comments.forEach(comment => {
    mergedCommentById.set(comment.id, comment)

    if (comment.prevId !== null) {
      appendDependentId(dependentIdsByPrevId, comment.prevId, comment.id)
    }

    if (comment.nextId !== null) {
      appendDependentId(dependentIdsByNextId, comment.nextId, comment.id)
    }
  })

  comments.forEach(comment => {
    if (getCommentAnchor(comment, renderedIds) !== null) {
      enqueueCandidate(comment.id)
    }
  })

  let candidateIndex = 0

  while (candidateIndex < candidateIds.length) {
    const candidateId = candidateIds[candidateIndex]!
    candidateIndex += 1

    queuedCandidateIds.delete(candidateId)

    const candidateComment = pendingCommentById.get(candidateId)
    if (!candidateComment) {
      continue
    }

    const anchor = getCommentAnchor(candidateComment, renderedIds)
    if (anchor === null) {
      continue
    }

    insertCommentId(orderedIds, candidateId, anchor)
    renderedIds.add(candidateId)
    pendingCommentById.delete(candidateId)

    dependentIdsByPrevId.get(candidateId)?.forEach(dependentId => enqueueCandidate(dependentId))
    dependentIdsByNextId.get(candidateId)?.forEach(dependentId => enqueueCandidate(dependentId))
  }

  const mergedComments = materializeRenderedComments(orderedIds, mergedCommentById)

  comments.forEach(comment => {
    if (pendingCommentById.has(comment.id)) {
      mergedComments.push(comment)
    }
  })

  return mergedComments
}

export const mergeComments = (
  localComments: CommentInterface[],
  serverComments: CommentInterface[],
): CommentInterface[] => {
  if (serverComments.length === 0) {
    return [...localComments]
  }

  const uniqueServerComments = buildUniqueServerComments(serverComments)
  const localCommentIds = new Set(localComments.map(comment => comment.id))
  const serverCommentById = new Map(uniqueServerComments.map(comment => [comment.id, comment]))
  const mergedLocalComments = localComments.map(
    localComment => serverCommentById.get(localComment.id) ?? localComment,
  )

  const comments = uniqueServerComments.filter(comment => !localCommentIds.has(comment.id))
  if (comments.length === 0) {
    return mergedLocalComments
  }

  return (
    tryMergeCommentFragments(mergedLocalComments, comments, localCommentIds) ??
    mergeCommentsWithDependencyQueue(mergedLocalComments, comments)
  )
}
